/**
 * Automation Profile schema and built-in presets.
 *
 * A profile is a small bundle of fields that controls per-state actor and
 * pause-gate behavior. Profiles are stored as data; users can author custom
 * profiles in addition to the built-ins.
 */
import { z } from 'zod';

export const ActorSchema = z.enum(['ai', 'human']);
export type ActorChoice = z.infer<typeof ActorSchema>;

export const ProfileSchema = z.object({
  /** Slug — unique identifier used in URLs and code. */
  slug: z.string().regex(/^[a-z][a-z0-9_-]*$/),
  /** Human-readable name. */
  name: z.string(),
  description: z.string().default(''),
  triage_actor: ActorSchema,
  plan_actor: ActorSchema,
  execute_actor: ActorSchema,
  approval_required: z.boolean(),
  review_required: z.boolean(),
  allow_clarification: z.boolean(),
  /** True for system-shipped presets that cannot be deleted. */
  built_in: z.boolean().default(false),
});

export type Profile = z.infer<typeof ProfileSchema>;

/** Per-task overrides on top of the chosen profile. */
export const ProfileOverridesSchema = z.object({
  triage_actor: ActorSchema.optional(),
  plan_actor: ActorSchema.optional(),
  execute_actor: ActorSchema.optional(),
  approval_required: z.boolean().optional(),
  review_required: z.boolean().optional(),
  allow_clarification: z.boolean().optional(),
});

export type ProfileOverrides = z.infer<typeof ProfileOverridesSchema>;

/** Compute the effective profile for a task by overlaying overrides. */
export function effectiveProfile(profile: Profile, overrides?: ProfileOverrides | null): Profile {
  if (!overrides) return profile;
  return {
    ...profile,
    triage_actor: overrides.triage_actor ?? profile.triage_actor,
    plan_actor: overrides.plan_actor ?? profile.plan_actor,
    execute_actor: overrides.execute_actor ?? profile.execute_actor,
    approval_required: overrides.approval_required ?? profile.approval_required,
    review_required: overrides.review_required ?? profile.review_required,
    allow_clarification: overrides.allow_clarification ?? profile.allow_clarification,
  };
}

// ---------------------------------------------------------------------------
// Built-in presets — seeded into the database on first migration.
// ---------------------------------------------------------------------------

export const BUILT_IN_PROFILES: Profile[] = [
  {
    slug: 'all-manual',
    name: 'All Manual',
    description: 'Pure task tracking. No AI involvement at any state.',
    triage_actor: 'human',
    plan_actor: 'human',
    execute_actor: 'human',
    approval_required: false,
    review_required: false,
    allow_clarification: false,
    built_in: true,
  },
  {
    slug: 'all-automatic',
    name: 'All Automatic',
    description: 'Fully automated. AI handles every work state. No gates.',
    triage_actor: 'ai',
    plan_actor: 'ai',
    execute_actor: 'ai',
    approval_required: false,
    review_required: false,
    allow_clarification: true,
    built_in: true,
  },
  {
    slug: 'plan-and-review',
    name: 'Plan & Review',
    description: 'AI handles every state, but human approves the plan and reviews the output.',
    triage_actor: 'ai',
    plan_actor: 'ai',
    execute_actor: 'ai',
    approval_required: true,
    review_required: true,
    allow_clarification: true,
    built_in: true,
  },
  {
    slug: 'eyes-on-auto',
    name: 'Eyes-On Auto',
    description: 'AI plans and executes without approval, but human reviews the output.',
    triage_actor: 'ai',
    plan_actor: 'ai',
    execute_actor: 'ai',
    approval_required: false,
    review_required: true,
    allow_clarification: true,
    built_in: true,
  },
  {
    slug: 'plan-it-yourself',
    name: 'Plan-It-Yourself',
    description: 'Human writes the plan, AI executes it, human reviews.',
    triage_actor: 'ai',
    plan_actor: 'human',
    execute_actor: 'ai',
    approval_required: false,
    review_required: true,
    allow_clarification: true,
    built_in: true,
  },
];
